import React from 'react';
import PropTypes from 'prop-types';
import {Form as SemanticForm, Popup} from 'semantic-ui-react';
import classnames from 'classnames';

import {convertToInt} from '@utils/helpers';

import {ReduxComponent} from './ReduxComponent';

/**
 * Render list of checkboxes from given array
 */
export class SemanticCheckboxList extends ReduxComponent {
    /**
     * @property {Array} defaultValue Array of numbers
     * @property {Boolean} hidden Param for hidding list
     * @property {String} name Name for checkboxes fields
     * @property {Array<Object>} options Array of Options<br/>
     * <pre>
     * [
     *  {
     *      key: 1
     *      value: "Value 1"
     *  },
     *  {
     *      key: 2
     *      value: "Value 2"
     *  }
     *  ...
     * ]
     * </pre>
     */
    static propTypes = {
        className: PropTypes.string,
        defaultValue: PropTypes.arrayOf(PropTypes.number),
        hidden: PropTypes.bool,
        input: PropTypes.object.isRequired,
        meta: PropTypes.object.isRequired,
        name: PropTypes.string,
        options: PropTypes.arrayOf(PropTypes.object),
        timestamp: PropTypes.number,
    };

    static defaultProps = {
        className: null,
        defaultValue: [],
        hidden: false,
        name: null,
        options: [],
        timestamp: null,
    };

    /**
     * @ignore
     */
    constructor(props) {
        super(props);

        this.renderContent = this.renderContent.bind(this);
        this.toggle = this.toggle.bind(this);

        let selected = [];

        if (!props.meta.visited) {
            selected = props.defaultValue;

            this.props.input.onChange(selected);
        }

        /**
         * @ignore
         */
        this.state = {
            selected : selected,
            options: props.options,
            timestamp: props.timestamp,
        };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        if (nextProps.timestamp !== prevState.timestamp) {
            nextProps.input.onChange(nextProps.defaultValue);

            return {
                selected: nextProps.defaultValue,
                timestamp: nextProps.timestamp,
            };
        }

        if (!nextProps.meta.visited) {
            const selected = nextProps.defaultValue;

            nextProps.input.onChange(selected);

            return {
                selected: selected,
            };
        }

        return null;
    }

    /**
     * @ignore
     */
    toggle(event){
        let inputData = this.state.selected.slice(0);
        const value = parseInt(event.currentTarget.dataset.value, 10);
        const inputIndex = inputData.indexOf(value);

        if ('radio' === this.props.type) {
            if (-1 === inputIndex || 0 === inputIndex) {
                inputData = [value];
            } else {
                inputData = [];
            }
        } else {
            if (-1 === inputIndex) {
                inputData.push(value);
            } else {
                inputData.splice(inputIndex, 1);
            }
        }

        this.setState(() => ({
            selected: inputData,
        }));

        this.props.input.onFocus();
        this.props.input.onChange(inputData);
        this.props.input.onBlur();

        if(this.props.onCheckboxChange) {
            this.props.onCheckboxChange(null, {name: this.props.input.name, data: inputData});
        }
    }

    /**
     * @ignore
     */
    renderContent() {
        const contents = [];
        const elementType = !this.props.type ? 'checkbox' : this.props.type;

        for (let i = 0; i < this.props.options.length; i++)  {
            const option = this.props.options[i];
            const baseLabel = option.text || option.key;
            const labelNode = option.tooltip
                // eslint-disable-next-line jsx-a11y/label-has-for
                ? (<label>
                    <Popup
                        trigger={<span>{baseLabel}</span>}
                        position={'right center'}
                        hoverable={true}
                        content={option.tooltip}
                    />
                </label>)
                : baseLabel;

            contents.push(
                <div key={option.key + i}>
                    <SemanticForm.Checkbox
                        className={this.props.className}
                        checked={
                            this.state.selected
                                ? -1 !== this.state.selected.indexOf(convertToInt(option.value))
                                : false
                        }
                        data-value={convertToInt(option.value)}
                        disabled={this.props.meta.submitting || option.disabled || this.props.disabled}
                        label={labelNode}
                        name={`${this.props.name}[${option.key}]`}
                        onChange={this.toggle}
                        {...{[elementType]: true}}
                    />
                    {option.popup}
                </div>
            );
        }

        return contents;
    }

    /**
     * @return
     * Store:<br/>
     * Array of numbers - [1, 4, 25]<br/><br/>
     * Html:
     * <pre>
     * < div class="{null | hidden}">
     *   Label
     *   < div className="input-container">
     *      < div class="field">
     *          < div data-value="1"
     *          class="ui checkbox">
     *              < input type="checkbox"
     *                class="hidden"
     *                name="[Johnpaul Thompson MD > Mr. Sylvester Watsica]"
     *                readonly=""
     *                tabindex="0"
     *                value="on">
     *              < label>Johnpaul Thompson MD &gt; Mr. Sylvester Watsica</ label>
     *           </ div>
     *      </ div>
     *      < div class="field">
     *          < div data-value="2"
     *          class="ui checkbox">
     *              < input type="checkbox"
     *                class="hidden"
     *                name="[Mr. Sylvester Watsica]"
     *                readonly=""
     *                tabindex="0"
     *                value="on">
     *              < label>Mr. Sylvester Watsica</ label>
     *           </ div>
     *      </ div>
     *      < div class="error">{Error information}</div>
     *   </ div>
     * </ div>
     * </pre>
     */
    render() {
        return (
            <div className={classnames({
                'hidden': this.props.hidden,
            })}>
                {this.renderLabel(this.props)}
                <div className={classnames('input-container', {'--inline': this.props.inline})}>
                    <div className='formFields'>{this.renderContent()}</div>
                    <div className='error'>{this.isError(this.props) ? this.props.meta.error : ''}</div>
                </div>
            </div>
        );
    }
}

SemanticCheckboxList.displayName = 'SemanticCheckboxList';

export default SemanticCheckboxList;
